import React, { Component, ChangeEvent, MouseEvent, FormEvent } from 'react'
import { Product as ProductPb } from '../../rpc/admin/products_pb';
import feather from '../../img/feather-sprite.svg';
import davidsons from '../../img/logos/davidsons.png';
import rsr from '../../img/logos/rsr.jpg';
import sportsSouth from '../../img/logos/sports-south.jpg';
import zanders from '../../img/logos/zanders.png';
import { formatCurrency } from '../../helpers/format';
import Notification from '../notification';
import { Supplier, GetSupplierInventoriesRequest, GetSupplierInventoriesResponse } from '../../rpc/admin/suppliers_pb';
import { Link } from 'react-router-dom';
import Select from '../forms/select';
import { ProductContext } from './context';
import { AdminServiceClient } from '../../rpc/admin/AdminServiceClientPb';
import * as grpcWeb from 'grpc-web';
import { convertErrorMessage, getAuthMetadata } from '../../helpers/rpc';
import { logOut } from '../../helpers/login';
import { getBaseApiUrl } from '../../helpers/instance';

interface SuppliersProps {
    values: Array<ProductPb.Variant.SupplierInventory>;
    disabled?: boolean;
    onChange?: (values: Array<ProductPb.Variant.SupplierInventory>) => void;
}

interface SuppliersState {
    editing: boolean;
    supplier: Supplier;
    sku: string;
    errorMessage: string;
    supplierError: string;
    skuError: string;
    index: number;
    loading: boolean;
}

class Suppliers extends Component<SuppliersProps, SuppliersState> {

    constructor(props: SuppliersProps) {
        super(props);
        this.state = {
            editing: false,
            supplier: Supplier.SUPPLIER_DAVIDSONS,
            sku: '',
            errorMessage: '',
            supplierError: '',
            skuError: '',
            index: -1,
            loading: false
        };
    }

    private handleKeyDown = (e: KeyboardEvent): any => {
        if (e.key === 'Escape') {
            this.cancel();
        } else if (e.key === 'Enter') {
            this.ok();
        }
    }

    private handleSkuChange = (e: ChangeEvent<HTMLInputElement>) => {
        this.setState({
            sku: e.target.value
        });
    }

    private handleSupplierChange = (s: Supplier) => {
        this.setState({
            supplier: s
        });
    }

    private handleEdit = (index: number) => {
        if (!this.state.editing) {
            window.addEventListener('keydown', this.handleKeyDown);
            if (index >= 0) {
                this.setState({
                    editing: true,
                    supplier: this.props.values[index].getSupplier(),
                    sku: this.props.values[index].getSku(),
                    errorMessage: '',
                    supplierError: '',
                    skuError: '',
                    index: index
                });
            } else {
                this.setState({
                    editing: true,
                    supplier: Supplier.SUPPLIER_DAVIDSONS,
                    sku: '',
                    errorMessage: '',
                    supplierError: '',
                    skuError: '',
                    index: index
                });
            }
        }
    }

    private handleDelete = (index: number) => {
        const updated: Array<ProductPb.Variant.SupplierInventory> = [];
        for (let i = 0; i < this.props.values.length; i++) {
            if (i !== index) {
                updated.push(this.props.values[i]);
            }
        }
        if (this.props.onChange) {
            this.props.onChange(updated);
        }
    }

    private handleOkClick = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        this.ok();
    }

    private handleCancelClick = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        this.cancel();
    }

    private ok = () => {
        let supplierError = '', skuError = '';
        const sku = this.state.sku.trim();
        if (this.state.supplier === Supplier.SUPPLIER_UNKNOWN) {
            supplierError = 'Supplier is required';
        }
        if (!sku) {
            skuError = 'SKU is required';
        }
        if (supplierError || skuError) {
            this.setState({
                supplierError: supplierError,
                skuError: skuError
            })
            return;
        }

        this.setState({
            loading: true
        });

        const client = new AdminServiceClient(getBaseApiUrl(), null, null);
        const request = new GetSupplierInventoriesRequest();
        const supplierRequest = new GetSupplierInventoriesRequest.Request();
        supplierRequest.setSupplier(this.state.supplier);
        supplierRequest.setSku(this.state.sku);
        request.setRequestsList([supplierRequest]);
        client.getSupplierInventories(request, getAuthMetadata(), (err: grpcWeb.Error, response: GetSupplierInventoriesResponse) => {
            this.setState({
                loading: false
            });
            if (!err) {
                const list = response.getInventoriesList();
                const values = this.props.values;
                if (list.length === 1) {
                    const value = new ProductPb.Variant.SupplierInventory();
                    value.setSupplier(list[0].getSupplier());
                    value.setSku(list[0].getSku());
                    value.setQoh(list[0].getQoh());
                    value.setPrice(list[0].getPrice() / 100);
                    value.setName(list[0].getName());
                    if (this.state.index >= 0) {
                        values[this.state.index] = value;
                    } else {
                        values.push(value);
                    }
                    if (this.props.onChange) {
                        this.props.onChange(values);
                    }
                    this.setState({
                        editing: false
                    });
                } else {
                    this.setState({
                        errorMessage: 'Item not found',
                    });
                }
            } else {
                if (err.code === grpcWeb.StatusCode.UNAUTHENTICATED || err.code === grpcWeb.StatusCode.PERMISSION_DENIED) {
                    logOut();
                } else {
                    this.setState({
                        errorMessage: convertErrorMessage(err),
                    });
                    console.error(err);
                }
            }
        });

    }

    private cancel = () => {
        window.removeEventListener('keydown', this.handleKeyDown);

        this.setState({
            editing: false
        });
    }

    private supplierImage = (s: Supplier): JSX.Element => {
        switch (s) {
            case Supplier.SUPPLIER_DAVIDSONS:
                return <img src={davidsons} alt="Davidsons" />
            case Supplier.SUPPLIER_RSR:
                return <img src={rsr} alt="RSR" />
            case Supplier.SUPPLIER_SPORTS_SOUTH:
                return <img src={sportsSouth} alt="Sports South" />
            case Supplier.SUPPLIER_ZANDERS:
                return <img src={zanders} alt="Zanders" />
        }
        return <></>;
    }

    private supplierLink = (s: Supplier, sku: string): JSX.Element => {
        switch (s) {
            case Supplier.SUPPLIER_DAVIDSONS:
                return <Link to={"/davidsons/" + encodeURIComponent(sku)}>{sku}</Link>
            case Supplier.SUPPLIER_RSR:
                return <Link to={"/rsr/" + encodeURIComponent(sku)}>{sku}</Link>
            case Supplier.SUPPLIER_SPORTS_SOUTH:
                return <Link to={"/sports-south/" + encodeURIComponent(sku)}>{sku}</Link>
            case Supplier.SUPPLIER_ZANDERS:
                return <Link to={"/zanders/" + encodeURIComponent(sku)}>{sku}</Link>
        }
        return <></>;
    }

    render() {
        let index = -1;
        return (
            <ProductContext.Consumer>
                {ctx => (
                    <>
                        <table className="table supplier-table">
                            <thead>
                                <tr>
                                    <th>Inventory Source</th>
                                    <th>Item</th>
                                    <th>SKU</th>
                                    <th>QOH</th>
                                    <th>Price</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    this.props.values.map((source) => {
                                        index++;
                                        return (
                                            <tr key={source.getSupplier() + source.getSku()}>
                                                <td>{this.supplierImage(source.getSupplier())}</td>
                                                <td className="supplier-name">{source.getName()}</td>
                                                <td>{this.supplierLink(source.getSupplier(), source.getSku())}</td>
                                                <td>{source.getQoh()}</td>
                                                <td>{formatCurrency(source.getPrice())}</td>
                                                <td className="has-text-right supplier-actions">
                                                    <button className="button is-small"
                                                        title="Edit"
                                                        disabled={this.props.disabled}
                                                        onClick={(e: FormEvent<HTMLButtonElement>) => this.handleEdit(index)}>
                                                        <svg width="16" height="16" className="button-icon">
                                                            <use href={feather + "#edit-2"}/>
                                                        </svg>
                                                    </button>
                                                    <button className="button is-small"
                                                        title="Delete"
                                                        disabled={this.props.disabled}
                                                        onClick={(e: FormEvent<HTMLButtonElement>) => this.handleDelete(index)}>
                                                        <svg width="16" height="16" className="button-icon">
                                                            <use href={feather + "#trash"}/>
                                                        </svg>
                                                    </button>
                                                </td>
                                            </tr>
                                        )
                                    })
                                }
                                <tr>
                                    <td colSpan={4}>
                                        <button className="button is-link is-small"
                                            onClick={(e: FormEvent<HTMLButtonElement>) => this.handleEdit(-1)}>Add Inventory Source</button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        {
                            this.state.editing && (
                                <div className="modal is-active">
                                    <div className="modal-background"></div>
                                        <div className="supplier-modal">
                                            <Notification className="supplier-notification" message={this.state.errorMessage} />
                                            <div className="supplier-modal-controls">
                                                <Select<Supplier> label="Source"
                                                    value={this.state.supplier}
                                                    options={ctx.supplierOptions}
                                                    errorMessage={this.state.supplierError}
                                                    onChange={this.handleSupplierChange} />

                                                <div className="field">
                                                    <label className="label">SKU</label>
                                                    <div className="control">
                                                        <input className={"input" + (this.state.skuError ? " is-danger" : "")}
                                                            autoComplete="off"
                                                            onChange={this.handleSkuChange}
                                                            type="text"
                                                            disabled={this.props.disabled}
                                                            value={this.state.sku} />
                                                    </div>
                                                    { this.state.skuError &&
                                                        <p className="help is-danger">{this.state.skuError}</p>
                                                    }
                                                </div>

                                                <button className={"button is-success" + (this.state.loading ? " is-loading" : "")}
                                                    onClick={this.handleOkClick}
                                                    disabled={this.state.loading}>OK</button>
                                                <button className="button" onClick={this.handleCancelClick} disabled={this.state.loading}>Cancel</button>
                                            </div>
                                        </div>
                                </div>
                            )
                        }
                    </>
                )}
            </ProductContext.Consumer>
        );
    }
}

export default Suppliers;
